import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../services/services';
import Table from '../../../components/common/table/table';
import DateFilter, {
  DateFields,
} from '../../../components/common/table/date-filter/date-filter';
import css from './index.module.css';
import Remainder from '../../../interfaces/remainder';
import { DefaultFilter } from '../../../components/common/table/default-filter';
import toFixed from 'accounting-js/lib/toFixed.js';
import { ListParams } from '../../../services/http-client';
import ExcelButton from '../../../components/common/table/excel-button/excel-button';
import { useQuery } from '../../../hooks/use-query';
import { WAREHOUSE_MOVEMENT_INDEX } from '../../../router/routes';
import { isValidDateSting, route, routeQuery } from '../../../services/utils';
import TabsKeys from '../../../enums/tab-keys';

export default function Remainders() {
  const { t } = useTranslation();
  const { farmId } = useParams<{ farmId: string }>();
  const { dealService, remainderService } = useService();

  const history = useHistory();
  const [params] = useQuery();
  const [init, setInit] = useState<boolean>(true);
  const dateFilterName = 'warehouse-movement-reminders';

  const [productCategories, setProductCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);

  // update "to date" filter value from storage or remove it from storage, if date value is not valid
  useEffect(() => {
    if (init && !params[DateFields.to]) {
      const filterName = dateFilterName.concat('-', DateFields.to);

      let toDate = sessionStorage.getItem(filterName);

      if (toDate) {
        if (isValidDateSting(toDate)) {
          const search = new URLSearchParams(history.location.search);
          search.set(DateFields.to, toDate);

          history.replace({ ...history.location, search: search.toString() });
        } else {
          sessionStorage.removeItem(filterName);
        }
      }
    }
    setInit(false);
  }, [init, history, params]);

  useEffect(() => {
    const $categories = dealService
      .usedProductCategories(farmId)
      .subscribe((categories) => {
        setProductCategories(
          categories.map((category) => ({
            label: category.value,
            value: category.id,
          }))
        );
      });

    const $productTypes = dealService
      .usedProductTypes(farmId)
      .subscribe((types) => {
        setProductTypes(
          types.map((type) => ({ label: type.value, value: type.value }))
        );
      });

    return () => {
      $categories.unsubscribe();
      $productTypes.unsubscribe();
    };
  }, [dealService, farmId, t]);

  const request = useCallback(
    (
      page: number,
      sort: string,
      desc: boolean,
      filter: { [key: string]: any }
    ) => remainderService.index(farmId, { page, sort, desc, ...filter }),
    [remainderService, farmId]
  );

  const toolbar = useCallback(
    (params: ListParams): JSX.Element => {
      return (
        <div className={css['balance-topbar']}>
          <div>
            <span>{t('pages.warehouse.remainders.to')}:</span>
            <DateFilter name={dateFilterName} fields={[DateFields.to]} />
          </div>

          <div className={css['date-with-export']}>
            <ExcelButton
              onClick={() =>
                remainderService.exportXls(farmId, params).subscribe()
              }
            />
          </div>
        </div>
      );
    },
    [farmId, remainderService, t]
  );

  return (
    <div className={css['body']}>
      <Table<Remainder>
        request={request}
        toolbar={toolbar}
        keyAccessor={(v: Remainder) =>
          `${v.warehouse_id}_${v.product_id}_${v.unit}`
        }
        columns={[
          {
            accessor: 'product.category.value',
            header: t('pages.warehouse.remainders.product_category'),
            sortable: true,
            filter: (
              <DefaultFilter
                options={productCategories}
                name="product_category_id"
              />
            ),
            className: css['strong'],
          },
          {
            accessor: 'product.type.value',
            header: t('pages.warehouse.remainders.product_type'),
            filter: (
              <DefaultFilter options={productTypes} name="product_type_value" />
            ),
            sortable: true,
            className: css['strong'],
          },
          {
            accessor: 'product.name',
            header: t('pages.warehouse.remainders.product_name'),
            sortable: true,
            className: css['strong'],
          },
          {
            accessor: 'product.obtain_type_id',
            header: t('pages.warehouse.remainders.product_obtain_type'),
            translationPrefix: 'classifiers.',
            translatable: (key: string) => {
              return t(key);
            },
            sortable: true,
            className: css['strong'],
          },
          {
            accessor: 'warehouse.name',
            header: t('pages.warehouse.remainders.warehouse'),
            sortable: true,
          },
          {
            accessor: 'amount',
            header: t('pages.warehouse.remainders.amount'),
            sortable: true,
            valuable: (value, remainder: Remainder) => {
              return `${
                Number.isInteger(value) ? value : toFixed(parseFloat(value), 3)
              } ${remainder.unit}`;
            },
          },
        ]}
        viewRoute={(remainder: Remainder) => {
          return routeQuery(
            route(WAREHOUSE_MOVEMENT_INDEX, {
              farmId: farmId,
              tab: TabsKeys.deals,
              product: remainder.product.id,
            }),
            {
              page: 1,
              product_name: remainder.product.name,
              product_category: remainder.product.category_id,
            }
          );
        }}
        viewRecordAbility={{ title: 'viewAny deal' }}
      />
    </div>
  );
}
